import React, { useEffect, useState } from 'react';
import Button from 'src/components/Button/Button';
import { PaymentMethodForm } from './PaymentMethodForm';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import AddButtonIcon from 'src/assets/img/add-button-icon.svg';
import { getEnvironment } from 'src/env/env';
import { loadStripe } from '@stripe/stripe-js';
import EditButtonIcon from 'src/assets/img/edit-button-icon.svg';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import CloseIcon from 'src/assets/img/close-icon';
import { DeletePopup } from 'src/components/DeletePopup/DeletePopup';
import { useDeletePaymentMethodsMutation, useSetDefaultPaymentMethodMutation } from 'src/services';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { PaymentMethodDetails } from 'src/pages/SubscriptionPage/PaymentMethod/AddPaymentMethodPopup/PaymentMethodDetails';

export const AddPaymentMethodPopup = ({
  paymentMethods,
  disabled = false,
  defaultOpen = false,
  handleCreateSuccess = () => {},
  trigger,
  container,
  submitBtnText,
}) => {
  const [open, setOpen] = useState(false);
  const [openTrigger, setOpenTrigger] = useState('');

  const [stripe, setStripe] = useState(null);

  const notification = useNotification();
  const loader = useLoader();

  const [
    setDefaultPaymentMethod,
    { isLoading: isLoadingSetDefaultPaymentMethod, isSuccess: isSetDefaultPaymentMethodSuccess },
  ] = useSetDefaultPaymentMethodMutation();

  const [deletePaymentMethod, { isSuccess: deletePaymentMethodIsSuccess }] =
    useDeletePaymentMethodsMutation();

  useEffect(() => {
    if (isLoadingSetDefaultPaymentMethod) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingSetDefaultPaymentMethod]);

  useEffect(() => {
    if (deletePaymentMethodIsSuccess) {
      notification.success('Payment method was deleted successfully.');
    }
  }, [deletePaymentMethodIsSuccess]);

  useEffect(() => {
    if (!stripe && open) {
      (async () => {
        const { APP_STRIPE_PUBLIC_KEY } = await getEnvironment();
        const stripeObject = await loadStripe(APP_STRIPE_PUBLIC_KEY);

        setStripe(stripeObject);
      })();
    }
  }, [stripe, open]);

  const accordionHandler = (trigger) => {
    setOpenTrigger(trigger);
  };
  const accordionHandlerClose = () => {
    setOpenTrigger('');
  };

  const handlePaymentMethodClick = (id, notify = true) => {
    setDefaultPaymentMethod({ payment_method_id: id }).then(() => {
      notify && notification.success('Payment method was changed successfully.');
    });
  };

  const handlePaymentMethodDelete = (id) => {
    deletePaymentMethod({ payment_method_id: id });
  };

  useEffect(() => {
    if (paymentMethods?.data?.length === 1 && !paymentMethods?.data[0].default) {
      handlePaymentMethodClick(paymentMethods?.data[0].id, false);
    }
  }, [paymentMethods]);

  useEffect(() => {
    if (defaultOpen) {
      accordionHandler('new-card');
    }
  }, [defaultOpen]);

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        {trigger ? (
          trigger
        ) : paymentMethods?.data?.length ? (
          <Button className="transparent">
            <span>Edit</span>
            <img src={EditButtonIcon} alt="edit icon" />
          </Button>
        ) : (
          <Button className="transparent">
            <p>Add</p>
            <img src={AddButtonIcon} alt="add icon" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="gap-5" container={container}>
        <DialogHeader className="mb-2.5">
          {paymentMethods?.data?.length ? (
            <DialogTitle>Edit payment method</DialogTitle>
          ) : (
            <DialogTitle>Add payment method</DialogTitle>
          )}
        </DialogHeader>
        <Accordion
          type="single"
          className="w-full"
          onValueChange={accordionHandler}
          value={openTrigger}
        >
          <AccordionItem value="new-card" className="border-b-0 rounded-sm-5 shadow bg-[#E9F7EC]">
            <AccordionTrigger
              hideDefaultIcon
              className="py-5 px-5 pr-7.5 opacity-80 transition hover:opacity-100"
            >
              <div className="flex items-center justify-between w-full">
                <p className="font-semibold text-lg text-[#2C964C]">Add new card</p>
                {(CloseIcon as any)({ className: 'rotate-45', stroke: '#2C964C' })}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5">
              <PaymentMethodForm
                submitBtnText={submitBtnText}
                stripe={stripe}
                handleClose={accordionHandlerClose}
                handleCreateSuccess={handleCreateSuccess}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <div
          className={clsx(
            openTrigger && 'opacity-40 pointer-events-none',
            'flex transition flex-col gap-2.5',
          )}
        >
          {Boolean(paymentMethods?.data?.length) &&
            paymentMethods?.data.map((paymentItem) => {
              const isDefault = paymentItem.default;
              return (
                <div
                  key={paymentItem.id}
                  onClick={() => !isDefault && handlePaymentMethodClick(paymentItem.id)}
                  className={clsx(
                    'transition w-full flex-col flex justify-between items-center rounded-sm-5 shadow bg-white hover:bg-opacity-100 [&>div]:hover:opacity-100 pl-5 pr-7.5 py-5',
                    !isDefault && 'cursor-pointer bg-opacity-50',
                  )}
                >
                  <div
                    className={clsx(
                      'flex justify-between items-center w-full transition',
                      !isDefault && 'opacity-40',
                    )}
                  >
                    <div className="flex items-center gap-2.5 transition">
                      <div className="font-medium table-fixed w-4">
                        <div className="col">
                          <div className="w-3 h-3 rounded-full relative border border-ebony">
                            <div
                              className={clsx(
                                'absolute w-[7.5px] h-[7.5px] transition rounded-full bg-ebony top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
                                isDefault ? 'block' : 'hidden',
                              )}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2.5 items-center">
                        <div className="col role flex-5 text-base">
                          <p className="font-medium text-base text-ebony opacity-70">
                            <span className="capitalize">{paymentItem.brand}</span> card
                            <span> ****{paymentItem.last4}</span>
                          </p>
                        </div>
                        <div className="rounded-full px-2 py-0.5 bg-[#B8C1C6]">
                          <p className="text-xs font-medium font-jamjuree text-white">
                            {paymentItem?.card_type}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <DeletePopup
                        title="Delete payment method?"
                        description={
                          <span className="font-medium text-base text-ebony opacity-70">
                            Are you sure you want to delete{' '}
                            <span className="capitalize">{paymentItem.brand}</span> card
                            <span> ****{paymentItem.last4}</span>
                          </span>
                        }
                        deleteHandler={() => handlePaymentMethodDelete(paymentItem.id)}
                      />
                    </div>
                  </div>
                  {isDefault && <PaymentMethodDetails paymentItem={paymentItem} />}
                </div>
              );
            })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
